import type { ServiceCode } from '@common/enums';
import type { Process } from '@common/interfaces';
import type {
    CaroPatient,
    CreateProcessRequestBody,
    CreateProcessResponseBody,
    DeleteProcessActionRequestBody,
    IGetPatientQuery,
    IGetProcessesQuery,
    PatientServiceContext,
    PerformProcessActionRequestBody,
} from '@common/interfaces/api/patientsApi';
import type { IPatient } from '@common/interfaces/patient';
import type { PerformProcessActionResult } from '@common/interfaces/performProcessAction';
import { fetchJson } from '@frontend/store/fetch';
import { getUrlQueryParameters } from './queryParameterUtils';

const baseUrl = '/api/patients';

export async function createProcess(
    patientId: string,
    data: CreateProcessRequestBody,
    signal: AbortSignal
): Promise<CreateProcessResponseBody> {
    const url = `${baseUrl}/${patientId}/processes`;
    const result = await fetchJson({ body: data, method: 'POST', signal, url });

    return result.json;
}

export async function getPatient(
    patientId: string,
    query: IGetPatientQuery | undefined,
    signal: AbortSignal
): Promise<IPatient> {
    const url = getUrlQueryParameters<IGetPatientQuery>(`${baseUrl}/${patientId}`, query);
    const result = await fetchJson({ method: 'GET', signal, url });

    return result.json;
}

export async function getProcesses(
    patientId: string,
    query: IGetProcessesQuery | undefined,
    signal: AbortSignal
): Promise<Process[]> {
    const url = getUrlQueryParameters<IGetProcessesQuery>(`${baseUrl}/${patientId}/processes`, query);
    const result = await fetchJson({ method: 'GET', signal, url });

    return result.json;
}

export async function performProcessAction(
    patientId: string,
    processId: string,
    data: PerformProcessActionRequestBody,
    signal: AbortSignal
): Promise<PerformProcessActionResult> {
    const url = `${baseUrl}/${patientId}/processes/${processId}`;
    const result = await fetchJson({ body: data, method: 'POST', signal, url });

    return result.json;
}

export async function deleteProcessAction(
    patientId: string,
    processId: string,
    processActionId: string,
    data: DeleteProcessActionRequestBody,
    signal: AbortSignal
): Promise<void> {
    const url = `${baseUrl}/${patientId}/processes/${processId}/actions/${processActionId}`;

    await fetchJson({ body: data, method: 'DELETE', signal, url });
}

export async function getCaroPatient(patientId: string, signal: AbortSignal): Promise<CaroPatient | null> {
    const url = `${baseUrl}/${patientId}/caroPatient`;
    const result = await fetchJson({ allowedHttpErrorCodes: [200, 404], method: 'GET', signal, url });

    if (result.status === 404) {
        return null;
    }

    return result.json;
}

export async function getServiceContext(
    patientId: string,
    serviceCode: ServiceCode,
    signal: AbortSignal
): Promise<PatientServiceContext> {
    const url = `${baseUrl}/${patientId}/services/${serviceCode}`;
    const result = await fetchJson({ method: 'GET', signal, url });

    return result.json;
}

export async function isHisDataAvailable(patientId: string) {
    const url = `/api/patients/${patientId}/hisDataAvailability`;

    const result = await fetchJson({ method: 'GET', url });

    return result.json.result === true;
}
